/**
 * Convert pesos to centavos
 * @param {number} pesos - Amount in pesos
 * @returns {number} Amount in centavos
 */
export function pesosTocentavos(pesos) {
  return Math.round(parseFloat(pesos) * 100);
}

/**
 * Convert centavos to pesos
 * @param {number} centavos - Amount in centavos
 * @returns {number} Amount in pesos
 */
export function centavosToPesos(centavos) {
  return parseFloat(centavos) / 100;
}

/**
 * Generate a slug from a string
 * @param {string} text - Text to convert to slug
 * @returns {string} Slugified text
 */
export function generateSlug(text) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove accents
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
}

/**
 * Convert JavaScript Date to ISO string for API
 * @param {Date|string} date - Date object or string
 * @returns {string|null} ISO string or null
 */
export function toISOString(date) {
  if (!date) return null;
  if (typeof date === "string") {
    return new Date(date).toISOString();
  }
  return date.toISOString();
}

/**
 * Convert ISO string to datetime-local input format
 * @param {string} isoString - ISO date string
 * @returns {string} Datetime-local format (YYYY-MM-DDTHH:mm)
 */
export function isoToLocalInput(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Validate precarga event dates
 * @param {object} data - Form data with dates
 * @returns {object|boolean} Validation errors or true if valid
 */
export function validatePrecargaDates(data) {
  const errors = {};
  
  const preloadOpen = data.preloadOpenAt ? new Date(data.preloadOpenAt) : null;
  const preloadClose = data.preloadCloseAt ? new Date(data.preloadCloseAt) : null;
  const refundOpen = data.refundOpenAt ? new Date(data.refundOpenAt) : null;
  const refundClose = data.refundCloseAt ? new Date(data.refundCloseAt) : null;

  // Preload window validation
  if (preloadOpen && preloadClose && preloadOpen >= preloadClose) {
    errors.preloadCloseAt = "La fecha de cierre debe ser posterior a la de apertura";
  }

  // Refund window validation
  if (refundOpen && refundClose && refundOpen >= refundClose) {
    errors.refundCloseAt = "La fecha de cierre debe ser posterior a la de apertura";
  }

  // Preload should be before refund
  if (preloadClose && refundOpen && preloadClose > refundOpen) {
    errors.refundOpenAt = "El periodo de devoluciones debe ser posterior al de precargas";
  }

  return Object.keys(errors).length > 0 ? errors : true;
}

/**
 * Validate amount limits
 * @param {object} data - Form data with limits
 * @returns {object|boolean} Validation errors or true if valid
 */
export function validatePrecargaLimits(data) {
  const errors = {};
  
  const minTopup = parseInt(data.minTopup);
  const maxTopup = parseInt(data.maxTopup);

  if (minTopup < 0) {
    errors.minTopup = "El mínimo no puede ser negativo";
  }

  if (maxTopup < 0) {
    errors.maxTopup = "El máximo no puede ser negativo";
  }

  if (minTopup > maxTopup) {
    errors.maxTopup = "El máximo debe ser mayor o igual al mínimo";
  }

  return Object.keys(errors).length > 0 ? errors : true;
}

/**
 * Format currency amount
 * @param {number} centavos - Amount in centavos
 * @param {string} currency - Currency code
 * @returns {string} Formatted amount
 */
export function formatCurrency(centavos, currency = "ARS") {
  const pesos = centavosToPesos(centavos);
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency,
  }).format(pesos);
}

/**
 * Get status badge variant
 * @param {string} status - Status string
 * @returns {string} Bootstrap variant
 */
export function getStatusVariant(status) {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "DISABLED":
      return "dark";
    default:
      return "dark";
  }
}

/**
 * Get status label in Spanish
 * @param {string} status - Status string
 * @returns {string} Translated status
 */
export function getStatusLabel(status) {
  switch (status) {
    case "ACTIVE":
      return "Activo";
    case "DISABLED":
      return "Deshabilitado";
    default:
      return status;
  }
}
